// src/firebase/orgBranding.js
// Per-org branding: a logo and header colour per role (student/marker/head
// marker), each optional and independently overridable - a tutor might only
// ever set the one shared logo/colour, or go further and tune all three.
// Deliberately separate from settings.js's orgs/{orgId}/settings/marking
// doc - branding must be readable by an anonymous visitor on the
// registration page, before anyone is signed in, so it lives in its own
// public-read orgBranding/{orgId} doc (see firestore.rules) rather than a
// doc gated to signed-in org members.
//
// Unlike examMaterials.js's upload helpers, this calls getDownloadURL after
// upload rather than storing only the Storage path - the result has to be
// directly usable as an <img src> on that public, pre-auth page.
//
// Legacy shape (pre-per-role branding) was a single top-level
// { logoPath, logoUrl }. That's kept as the "default" role below - an org
// that only ever uploaded one logo keeps working exactly as before, used as
// the fallback for any role that doesn't have its own override. Header
// colours have no legacy field; each role's own hardcoded default (in the
// component that renders it) is the fallback when unset here.

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './config';

const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2MB

// 'default' is the legacy single-logo slot, stored at the doc's top level
// for backward compatibility; the other three are per-role overrides nested
// under `logos`.
export const BRANDING_ROLES = ['default', 'student', 'marker', 'headMarker'];

const brandingRef = (orgId) => doc(db, 'orgBranding', orgId);

/**
 * Get an org's branding. Public read - safe to call before the caller is
 * signed in. `logoUrl` is the legacy/default logo; `logos`/`headerColors`
 * are per-role overrides (only the roles that have been set are present).
 */
export const getOrgBranding = async (orgId) => {
  try {
    const snap = await getDoc(brandingRef(orgId));
    if (!snap.exists()) return { logoUrl: null, logos: {}, headerColors: {} };
    const data = snap.data();
    return {
      logoUrl: data.logoUrl || null,
      logos: data.logos || {},
      headerColors: data.headerColors || {}
    };
  } catch (error) {
    console.error('Get org branding error:', error);
    return { logoUrl: null, logos: {}, headerColors: {} };
  }
};

/**
 * Upload a new logo for an org, replacing any existing one for that role.
 * role: 'default' | 'student' | 'marker' | 'headMarker'. Returns the new
 * logo URL.
 */
export const uploadOrgLogo = async (orgId, file, role = 'default') => {
  if (!BRANDING_ROLES.includes(role)) throw new Error(`Unknown branding role: ${role}`);
  if (file.size > MAX_LOGO_BYTES) {
    throw new Error('Logo image must be under 2MB.');
  }

  const existing = await getDoc(brandingRef(orgId));
  const existingData = existing.exists() ? existing.data() : {};
  const previousPath = role === 'default'
    ? existingData.logoPath
    : existingData.logos?.[role]?.path;

  // Best-effort cleanup of the previous file - never block the new upload on this.
  if (previousPath) {
    try {
      await deleteObject(ref(storage, previousPath));
    } catch (error) {
      console.error('Delete previous org logo error:', error);
    }
  }

  const logoPath = `orgLogos/${orgId}/${role}/${Date.now()}_${file.name}`;
  const fileRef = ref(storage, logoPath);
  await uploadBytesResumable(fileRef, file);
  const logoUrl = await getDownloadURL(fileRef);

  if (role === 'default') {
    await setDoc(brandingRef(orgId), { logoPath, logoUrl, updatedAt: serverTimestamp() }, { merge: true });
  } else {
    await setDoc(brandingRef(orgId), {
      logos: { [role]: { path: logoPath, url: logoUrl } },
      updatedAt: serverTimestamp()
    }, { merge: true });
  }

  return logoUrl;
};

/**
 * Remove an org's custom logo for one role, reverting it to the fallback
 * (the 'default' logo, or the generic built-in branding if that's unset too).
 */
export const removeOrgLogo = async (orgId, role = 'default') => {
  if (!BRANDING_ROLES.includes(role)) throw new Error(`Unknown branding role: ${role}`);

  const existing = await getDoc(brandingRef(orgId));
  const existingData = existing.exists() ? existing.data() : {};
  const previousPath = role === 'default'
    ? existingData.logoPath
    : existingData.logos?.[role]?.path;
  if (previousPath) {
    try {
      await deleteObject(ref(storage, previousPath));
    } catch (error) {
      console.error('Delete org logo error:', error);
    }
  }

  if (role === 'default') {
    await setDoc(brandingRef(orgId), { logoPath: null, logoUrl: null, updatedAt: serverTimestamp() }, { merge: true });
  } else {
    await setDoc(brandingRef(orgId), {
      logos: { [role]: { path: null, url: null } },
      updatedAt: serverTimestamp()
    }, { merge: true });
  }
};

/**
 * Set (or clear, passing null) a role's header bar colour override.
 * role: 'student' | 'marker' | 'headMarker' (no 'default' - each role
 * already has its own sensible hardcoded colour to fall back to).
 */
export const updateOrgHeaderColor = async (orgId, role, color) => {
  if (!['student', 'marker', 'headMarker'].includes(role)) throw new Error(`Unknown branding role: ${role}`);
  await setDoc(brandingRef(orgId), {
    headerColors: { [role]: color || null },
    updatedAt: serverTimestamp()
  }, { merge: true });
};
